import { useMemo } from 'react';
import { Booking, ResourceCompressed } from '@jooxter/api';
import { useFetchResource, useFetchResourceOptions, useFetchResourceTypes } from '../../../queries';
import { TResourceConfiguration } from '../types/booking-form.types';

/**
 * Everything the booking form is - its schema, its default values, which fields even exist - is a
 * function of the resource being booked, so the configuration is derived data and never state.
 *
 * The resource cannot change while the form is mounted: the resource select is rendered hidden, and
 * opening the form on another resource remounts JxtBookingFormContainer through its `key`. That is
 * what lets the whole chain (configuration -> default values -> schema -> form) stay a single
 * top-down computation instead of a set of effects feeding each other.
 */
export const useBookingResourceConfiguration = (
  resource?: ResourceCompressed,
  bookingToEdit?: Booking | null
): { resourceConfiguration?: TResourceConfiguration; isLoading: boolean } => {
  const source = (bookingToEdit?.resource ?? resource) as ResourceCompressed | undefined;
  const { resourceTypes, isLoading } = useFetchResourceTypes();
  const { resource: fetchedResource } = useFetchResource(source?.id);
  const { options } = useFetchResourceOptions(source?.id);

  const resourceConfiguration = useMemo(() => {
    const resourceType = resourceTypes?.find((r) => r.id === source?.resourceTypeId);

    if (!source || !resourceType) {
      return undefined;
    }

    return {
      timezone: source.timezone,
      guestsRequired: source.guestsRequired,
      physicalAttendeesRequired: source.physicalAttendeesRequired,
      isZone: resourceType.allowOverlap,
      metaType: resourceType.metaType,
      recurrence: source.capabilities?.recurrence,
      capacity: source.capacity,
      slots: fetchedResource?.slots,
      options,
    };
  }, [source, resourceTypes, fetchedResource?.slots, options]);

  return { resourceConfiguration, isLoading };
};
